import { useState, useRef, useEffect } from 'react';
import {
  Terminal,
  ChevronRight,
  ChevronDown,
  Loader2,
  Send,
  Wrench,
} from 'lucide-react';
import clsx from 'clsx';
import type { TerminalMessage } from '../../lib/tryit-types';

interface TerminalPanelProps {
  messages: TerminalMessage[];
  isStreaming: boolean;
  messagesRemaining: number;
  onSendMessage: (message: string) => void;
  bedrockAvailable: boolean;
}

function ToolUseCard({ msg }: { msg: TerminalMessage }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="ml-4 my-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-xs text-amber-400/80 hover:text-amber-300 transition-colors font-mono"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        <Wrench className="h-3 w-3" />
        <span>{msg.toolName}</span>
      </button>
      {expanded && msg.toolInput && (
        <pre className="ml-6 mt-1 text-[11px] text-gray-500 bg-black/30 rounded p-2 overflow-x-auto max-h-32">
          {JSON.stringify(msg.toolInput, null, 2)}
        </pre>
      )}
    </div>
  );
}

function ToolResultCard({ msg }: { msg: TerminalMessage }) {
  const [expanded, setExpanded] = useState(false);
  const preview =
    msg.content.length > 200
      ? msg.content.slice(0, 200) + '...'
      : msg.content;

  return (
    <div className="ml-4 my-1">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-xs text-green-500/70 hover:text-green-400 transition-colors font-mono"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        <span>Result from {msg.toolName}</span>
      </button>
      <pre
        className={clsx(
          'ml-6 mt-1 text-[11px] text-gray-500 bg-black/30 rounded p-2 overflow-x-auto',
          expanded ? 'max-h-80' : 'max-h-20',
        )}
      >
        {expanded ? msg.content : preview}
      </pre>
    </div>
  );
}

export default function TerminalPanel({
  messages,
  isStreaming,
  messagesRemaining,
  onSendMessage,
  bedrockAvailable,
}: TerminalPanelProps) {
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;
    setInput('');
    onSendMessage(trimmed);
  };

  return (
    <div className="flex flex-col h-full bg-[#1a1a1a] border-t border-dark-border overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-1.5 bg-[#252526] border-b border-dark-border">
        <div className="flex items-center gap-2">
          <Terminal className="h-3.5 w-3.5 text-gray-500" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
            Terminal
          </span>
          {isStreaming && (
            <Loader2 className="h-3 w-3 text-accent animate-spin" />
          )}
        </div>
        <span className="text-[10px] text-gray-600 font-mono">
          {messagesRemaining} messages left
        </span>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4 space-y-2 font-mono text-sm"
      >
        {messages.map((msg) => {
          switch (msg.type) {
            case 'system':
              return (
                <div key={msg.id} className="text-gray-600 text-xs italic">
                  {msg.content}
                </div>
              );
            case 'user':
              return (
                <div key={msg.id} className="text-gray-300">
                  <span className="text-accent">{'>'}</span> {msg.content}
                </div>
              );
            case 'assistant_text':
              return (
                <div
                  key={msg.id}
                  className="text-gray-300 whitespace-pre-wrap leading-relaxed"
                >
                  {msg.content}
                  {isStreaming &&
                    msg ===
                      messages.filter((m) => m.type === 'assistant_text').at(-1) && (
                      <span className="inline-block w-2 h-4 bg-accent/50 animate-pulse ml-0.5" />
                    )}
                </div>
              );
            case 'tool_use':
              return <ToolUseCard key={msg.id} msg={msg} />;
            case 'tool_result':
              return <ToolResultCard key={msg.id} msg={msg} />;
            case 'error':
              return (
                <div key={msg.id} className="text-red-400 text-xs">
                  Error: {msg.content}
                </div>
              );
            default:
              return null;
          }
        })}
      </div>

      {/* Input */}
      <form
        onSubmit={handleSubmit}
        className="flex items-center gap-2 px-4 py-2 border-t border-dark-border bg-[#1e1e1e]"
      >
        <span className="text-accent text-sm font-mono">{'>'}</span>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            !bedrockAvailable
              ? 'Bedrock not configured...'
              : isStreaming
                ? 'Waiting for response...'
                : messagesRemaining <= 0
                  ? 'Session limit reached'
                  : 'Ask Claude Code anything...'
          }
          disabled={isStreaming || !bedrockAvailable || messagesRemaining <= 0}
          className="flex-1 bg-transparent text-sm text-gray-200 font-mono outline-none placeholder:text-gray-600 disabled:opacity-50"
          autoFocus
        />
        <button
          type="submit"
          disabled={isStreaming || !input.trim() || !bedrockAvailable}
          className="p-1.5 text-gray-500 hover:text-accent disabled:opacity-30 transition-colors"
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
